import { Box, Dialog, MenuItem, Select, Stack, Typography } from '@mui/material';
import { LogOut, UserMinus } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { fetchOrgUsers } from '../../services/projectApi';
import { ButtonPush } from '../ui/ButtonPush';
import { ButtonStd } from '../ui/ButtonStd';
import CardTitle from '../ui/CardTitle';
import HandleTop from '../ui/HandleTop';
import InputStd from '../ui/InputStd';
import ScrollBox from '../ui/ScrollBox';
import WindowBackground from '../ui/WindowBackground';

const MembersModal = ({
  open,
  onClose,
  members,
  creatorUsername,
  currentUsername,
  isOwner,
  onAdd,
  onRemove,
  onLeave,
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [orgUsers, setOrgUsers] = useState([]);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveInput, setLeaveInput] = useState('');

  // Fetch org users when modal opens
  useEffect(() => {
    if (open) {
      fetchOrgUsers()
        .then((users) => setOrgUsers(users))
        .catch(() => setOrgUsers([]));
      setShowLeaveConfirm(false);
      setLeaveInput('');
      setSelectedUser('');
    }
  }, [open]);

  const handleAdd = async () => {
    if (!selectedUser) return;
    await onAdd?.(selectedUser);
    setSelectedUser('');
  };

  const handleLeave = async () => {
    if (leaveInput !== 'LEAVE') return;
    await onLeave?.();
    setShowLeaveConfirm(false);
    setLeaveInput('');
    onClose();
  };

  // Filter org users: not already a member, exclude giteaadmin
  const memberUsernames = new Set(members.map((m) => m.username));
  const availableUsers = orgUsers.filter(
    (u) => !memberUsernames.has(u.username) && u.username !== 'giteaadmin',
  );

  // Separate owner from members
  const owner = members.find((m) => m.username === creatorUsername);
  const regularMembers = members.filter((m) => m.username !== creatorUsername);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { background: 'transparent', boxShadow: 'none' } } }}
    >
      <Box
        sx={{
          width: 500,
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          borderRadius: '16px',
          backgroundColor: 'rgba(102, 111, 116, 0.6)',
          boxShadow: '0 24px 24px 0 rgba(67, 79, 86, 0.21)',
        }}
      >
        <HandleTop onIconClick={onClose} iconAriaLabel="Close" />
        <WindowBackground radius="20px">
          <CardTitle title="Project Members">
            {/* Owner */}
            {owner && (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  sx={{
                    color: '#7d8d97',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5,
                  }}
                >
                  Owner
                </Typography>
                <Box
                  sx={{
                    height: 36,
                    borderRadius: 1,
                    boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.15), 0 1px 0 0 #FFF',
                    bgcolor: '#f1f2f2',
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                  }}
                >
                  <Typography sx={{ color: '#394b57', fontWeight: 600, fontSize: 13 }}>
                    {owner.username}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Members list */}
            <Typography
              sx={{
                color: '#7d8d97',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                mb: 0.5,
              }}
            >
              Members
            </Typography>
            <ScrollBox sx={{ maxHeight: 200 }}>
              <Stack spacing={0.75}>
                {regularMembers.map((m) => (
                  <Box key={m.username} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      sx={{
                        flex: 1,
                        height: 36,
                        borderRadius: 1,
                        boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.15), 0 1px 0 0 #FFF',
                        bgcolor: '#f1f2f2',
                        display: 'flex',
                        alignItems: 'center',
                        px: 2,
                      }}
                    >
                      <Typography sx={{ color: '#394b57', fontWeight: 500, fontSize: 13 }}>
                        {m.username}
                      </Typography>
                    </Box>
                    {isOwner && (
                      <ButtonPush
                        width="36px"
                        height="36px"
                        tooltipText="Remove member"
                        onClick={() => onRemove?.(m.username)}
                      >
                        <UserMinus size={14} strokeWidth={1.5} />
                      </ButtonPush>
                    )}
                  </Box>
                ))}
                {regularMembers.length === 0 && (
                  <Typography sx={{ color: '#A9B6BC', fontSize: 12, py: 1, textAlign: 'center' }}>
                    No members added yet
                  </Typography>
                )}
              </Stack>
            </ScrollBox>

            {/* Add member (owner only) */}
            {isOwner && (
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  sx={{
                    color: '#7d8d97',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 0.5,
                  }}
                >
                  Add Member
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{
                      flex: 1,
                      height: 36,
                      fontSize: 13,
                      color: '#394b57',
                      bgcolor: '#f1f2f2',
                      borderRadius: 1,
                      boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.15), 0 1px 0 0 #FFF',
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <Typography sx={{ color: '#A9B6BC', fontSize: 13 }}>
                        Select a user...
                      </Typography>
                    </MenuItem>
                    {availableUsers.map((u) => (
                      <MenuItem key={u.username} value={u.username}>
                        {u.username}
                      </MenuItem>
                    ))}
                    {availableUsers.length === 0 && (
                      <MenuItem disabled>
                        <Typography sx={{ color: '#A9B6BC', fontSize: 13 }}>
                          No users available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                  <ButtonStd height="36px" onClick={handleAdd} disabled={!selectedUser}>
                    Add
                  </ButtonStd>
                </Stack>
              </Box>
            )}

            {/* Leave project (non-owner only) */}
            {!isOwner && (
              <Box sx={{ mt: 2, borderTop: '1px solid rgba(0,0,0,0.08)', pt: 1.5 }}>
                {!showLeaveConfirm ? (
                  <ButtonStd height="36px" onClick={() => setShowLeaveConfirm(true)}>
                    <LogOut size={14} strokeWidth={2} style={{ marginRight: 4 }} /> Leave Project
                  </ButtonStd>
                ) : (
                  <Stack spacing={1}>
                    <Typography sx={{ color: '#c62828', fontSize: 12, fontWeight: 500 }}>
                      Type LEAVE to confirm leaving this project. This action cannot be undone.
                    </Typography>
                    <InputStd
                      label=""
                      value={leaveInput}
                      onChange={(val) => setLeaveInput(val)}
                      align="left"
                      width="100%"
                    />
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <ButtonStd
                        height="36px"
                        onClick={handleLeave}
                        disabled={leaveInput !== 'LEAVE'}
                      >
                        Confirm
                      </ButtonStd>
                      <ButtonStd
                        height="36px"
                        onClick={() => {
                          setShowLeaveConfirm(false);
                          setLeaveInput('');
                        }}
                      >
                        Cancel
                      </ButtonStd>
                    </Stack>
                  </Stack>
                )}
              </Box>
            )}
          </CardTitle>
        </WindowBackground>
      </Box>
    </Dialog>
  );
};

export default MembersModal;
